import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import followUpContestService from '../services/followUpContestService';
import cdePlusLogo from '../assets/images/cde_plus_logo.png';
import { FaPaperPlane, FaArrowLeft, FaTimesCircle, FaUserCircle, FaCamera, FaImage, FaPaperclip, FaTimes, FaFile, FaFileImage } from 'react-icons/fa';

/* ─── Layout ─── */
const PageContainer = styled.div`
  min-height: 100vh;
  background: #e8e8e8;
  background-image:
    radial-gradient(circle at 20% 50%, rgba(220,38,38,0.03) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(220,38,38,0.03) 0%, transparent 50%),
    url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.02'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
`;

/* ─── Top Header ─── */
const TopHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 100;
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
  box-shadow: 0 2px 12px rgba(0,0,0,0.15);
`;

const TopHeaderInner = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 0.7rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const BackBtn = styled.button`
  background: rgba(255,255,255,0.1);
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 0.4rem;
  display: flex;
  align-items: center;
  border-radius: 8px;
  transition: all 0.15s;
  &:hover { background: rgba(255,255,255,0.2); }
`;

const HeaderLogo = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
  background: ${props => props.theme.primary};
  border-radius: 10px;
  padding: 3px;
`;

const HeaderTitle = styled.span`
  font-size: 0.95rem;
  font-weight: 700;
  color: #fff;
  flex: 1;
`;

const HeaderStatus = styled.span<{ status: string }>`
  font-size: 0.6rem;
  padding: 0.15rem 0.5rem;
  border-radius: 10px;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  background: ${p => p.status === 'Open' ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)'};
  color: ${p => p.status === 'Open' ? '#6ee7b7' : '#fca5a5'};
  border: 1px solid ${p => p.status === 'Open' ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'};
`;

/* ─── Info Banner ─── */
const InfoBanner = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 0.5rem 0.5rem 0;
`;

const BannerCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 0.6rem 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  border: 1px solid #e5e7eb;
  border-left: 3px solid ${props => props.theme.primary};
`;

const BannerAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, #fef2f2, #fee2e2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${props => props.theme.primary};
  font-size: 17px;
`;

const BannerDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const BannerFormNumber = styled.div`
  font-weight: 700;
  font-size: 0.78rem;
  color: #1a1a1a;
`;

const BannerBottomRow = styled.div`
  margin-top: 0.1rem;
  font-size: 0.65rem;
  color: #999;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CloseBtn = styled.button`
  background: none;
  color: #aaa;
  border: 1px solid #e5e5e5;
  padding: 0.25rem 0.5rem;
  border-radius: 8px;
  font-size: 0.6rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.2rem;
  flex-shrink: 0;
  transition: all 0.15s;
  text-transform: uppercase;
  letter-spacing: 0.3px;

  &:hover {
    border-color: #dc2626;
    color: #dc2626;
    background: #fef2f2;
  }
`;

/* ─── Chat Area ─── */
const ContentContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 140px);
`;

const ChatMessagesContainer = styled.div`
  flex: 1;
  border-radius: 12px;
  padding: 0.5rem;
  overflow-y: auto;
  margin-bottom: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

/* Date separator */
const DateSeparator = styled.div`
  text-align: center;
  margin: 0.5rem 0;
  font-size: 0.6rem;
  color: #999;
`;

const DateLabel = styled.span`
  background: white;
  padding: 0.2rem 0.75rem;
  border-radius: 10px;
  font-weight: 600;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04);
`;

/* Card-style messages */
const MessageCard = styled.div<{ isMine: boolean }>`
  max-width: 80%;
  align-self: ${props => props.isMine ? 'flex-end' : 'flex-start'};
  background: white;
  border-radius: 12px;
  border: 1px solid ${props => props.isMine ? '#e0e7ff' : '#e5e7eb'};
  border-left: ${props => props.isMine ? 'none' : '3px solid #d1d5db'};
  border-right: ${props => props.isMine ? '3px solid ${props.theme.primary || "#dc2626"}' : 'none'};
  border-right: ${props => props.isMine ? '3px solid #3b82f6' : 'none'};
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  transition: box-shadow 0.15s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  }
`;

const CardHeader = styled.div<{ isMine: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.65rem;
  background: ${props => props.isMine ? '#f0f4ff' : '#f9fafb'};
  border-bottom: 1px solid ${props => props.isMine ? '#e0e7ff' : '#f0f0f0'};
`;

const CardAvatar = styled.div<{ isMine: boolean }>`
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: ${props => props.isMine ? '#dbeafe' : '#f3f4f6'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${props => props.isMine ? '#3b82f6' : '#9ca3af'};
  font-size: 10px;
  font-weight: 700;
`;

const CardSenderName = styled.span<{ isMine: boolean }>`
  font-size: 0.65rem;
  font-weight: 700;
  color: ${props => props.isMine ? '#3b82f6' : '#6b7280'};
  flex: 1;
`;

const CardTime = styled.span`
  font-size: 0.55rem;
  color: #bbb;
  flex-shrink: 0;
`;

const CardBody = styled.div`
  padding: 0.5rem 0.65rem;
  font-size: 0.84rem;
  line-height: 1.5;
  color: #374151;

  img {
    max-width: 100%;
    max-height: 240px;
    border-radius: 8px;
    margin-top: 0.3rem;
    display: block;
  }

  a {
    color: #3b82f6;
    text-decoration: underline;
  }
`;

const AttachmentPreview = styled.div`
  padding: 0.3rem 0.65rem 0.5rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
`;

const AttachmentThumb = styled.a`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.25rem 0.5rem;
  background: #f3f4f6;
  border-radius: 6px;
  font-size: 0.65rem;
  color: #6b7280;
  text-decoration: none;
  border: 1px solid #e5e7eb;
  transition: all 0.15s;

  &:hover {
    background: #e5e7eb;
    color: #374151;
  }
`;

/* ─── Input Area ─── */
const InputArea = styled.div`
  background: white;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
`;

const InputRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.3rem;
  padding: 0.4rem 0.5rem;
`;

const MessageInput = styled.textarea`
  flex: 1;
  border: none;
  padding: 0.45rem 0.5rem;
  font-size: 0.85rem;
  resize: none;
  min-height: 36px;
  max-height: 100px;
  font-family: inherit;
  outline: none;
  background: transparent;
  color: #1a1a1a;

  &::placeholder { color: #bbb; }
`;

const InputActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.15rem;
  flex-shrink: 0;
`;

const IconBtn = styled.button`
  background: none;
  border: none;
  padding: 0.4rem;
  color: #999;
  cursor: pointer;
  display: flex;
  align-items: center;
  border-radius: 8px;
  transition: all 0.15s;

  &:hover {
    color: #555;
    background: #f3f4f6;
  }
`;

const SendButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 10px;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s;

  &:hover { background: #2563eb; }
  &:disabled { background: #d1d5db; cursor: not-allowed; }
`;

/* Attachment picker popup */
const AttachPopup = styled.div`
  position: absolute;
  bottom: 100%;
  left: 0.5rem;
  margin-bottom: 0.4rem;
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  overflow: hidden;
  z-index: 50;
`;

const AttachOption = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.6rem 1rem;
  background: none;
  border: none;
  font-size: 0.8rem;
  color: #374151;
  cursor: pointer;
  transition: background 0.1s;
  white-space: nowrap;

  &:hover { background: #f3f4f6; }
  &:not(:last-child) { border-bottom: 1px solid #f0f0f0; }

  svg { flex-shrink: 0; }
`;

/* File preview strip */
const FilePreviewStrip = styled.div`
  display: flex;
  gap: 0.4rem;
  padding: 0.4rem 0.6rem;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
  flex-wrap: wrap;
`;

const FilePreviewItem = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.5rem;
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  font-size: 0.68rem;
  color: #555;
  max-width: 180px;
`;

const FilePreviewThumb = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 4px;
  object-fit: cover;
`;

const FileRemoveBtn = styled.button`
  position: absolute;
  top: -5px;
  right: -5px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  font-size: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const EmptyChat = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #bbb;
  gap: 0.5rem;
  font-size: 0.85rem;
`;

const EmptyChatIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: white;
  border: 2px dashed #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  opacity: 0.5;
`;

const LoadingDots = keyframes`
  0%, 80%, 100% { opacity: 0.3; }
  40% { opacity: 1; }
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 2rem;
  color: #bbb;
  font-size: 0.85rem;

  &::after {
    content: '...';
    animation: ${LoadingDots} 1.4s infinite;
  }
`;

const ClosedBanner = styled.div`
  background: white;
  border-radius: 12px;
  padding: 0.7rem;
  text-align: center;
  color: #999;
  font-size: 0.8rem;
  border: 1px solid #e5e7eb;
`;

/* ─── Confirm Dialog ─── */
const DialogOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  backdrop-filter: blur(4px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const DialogBox = styled.div`
  background: white;
  border-radius: 20px;
  padding: 1.75rem 1.5rem;
  max-width: 320px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0,0,0,0.15);
  text-align: center;
`;

const DialogIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #fef2f2;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  font-size: 1.5rem;
`;

const DialogTitle = styled.h3`
  margin: 0 0 0.4rem;
  font-size: 1.05rem;
  color: #1a1a1a;
  font-weight: 700;
`;

const DialogMessage = styled.p`
  margin: 0 0 1.5rem;
  font-size: 0.82rem;
  color: #888;
  line-height: 1.5;
`;

const DialogButtons = styled.div`
  display: flex;
  gap: 0.6rem;
`;

const DialogCancelBtn = styled.button`
  flex: 1;
  padding: 0.65rem;
  border-radius: 12px;
  border: 1px solid #e5e5e5;
  background: white;
  color: #555;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { background: #f5f5f5; }
`;

const DialogConfirmBtn = styled.button`
  flex: 1;
  padding: 0.65rem;
  border-radius: 12px;
  border: none;
  background: #dc2626;
  color: white;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { background: #b91c1c; }
`;

/* ─── Component ─── */
const FollowUpContestChat: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const uid = searchParams.get('uid') || '';
  const type = searchParams.get('type') || 'FollowUp';

  const [header, setHeader] = useState<any>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentUserJPUID, setCurrentUserJPUID] = useState('');
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [closing, setClosing] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<string[]>([]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const prevMessageCountRef = useRef(0);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const inputAreaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try {
      const tokenData = localStorage.getItem('authToken');
      if (tokenData) {
        const parts = tokenData.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]));
          setCurrentUserJPUID(payload.JobPositionUID || payload.jobPositionUID || '');
        }
      }
    } catch (e) { /* fallback */ }
  }, []);

  // Close attach menu on outside click
  useEffect(() => {
    if (!showAttachMenu) return;
    const handler = (e: MouseEvent) => {
      if (inputAreaRef.current && !inputAreaRef.current.contains(e.target as Node)) {
        setShowAttachMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showAttachMenu]);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, []);

  const playNotificationSound = useCallback(() => {
    try {
      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      [800, 1000].forEach((freq, i) => {
        const osc = audioCtx.createOscillator();
        const gain = audioCtx.createGain();
        osc.connect(gain);
        gain.connect(audioCtx.destination);
        osc.frequency.value = freq;
        osc.type = 'sine';
        const start = audioCtx.currentTime + i * 0.15;
        gain.gain.setValueAtTime(0.2, start);
        gain.gain.exponentialRampToValueAtTime(0.01, start + 0.25);
        osc.start(start);
        osc.stop(start + 0.25);
      });
    } catch (e) { /* Audio not available */ }
  }, []);

  const loadMessages = useCallback(async (silent = false) => {
    if (!uid) return;
    try {
      if (!silent) setLoading(true);
      const getMessagesFn = type === 'FollowUp'
        ? followUpContestService.getFollowUpMessages(uid)
        : followUpContestService.getContestMessages(uid);

      const res = await getMessagesFn;
      const data = res?.Data || res?.data;
      if (data) {
        const headerData = data.Header || data.header;
        const messagesData = data.Messages || data.messages || [];
        setHeader(headerData);
        setMessages(messagesData);

        if (silent && messagesData.length > prevMessageCountRef.current && prevMessageCountRef.current > 0) {
          playNotificationSound();
        }
        prevMessageCountRef.current = messagesData.length;

        if (!silent || messagesData.length !== messages.length) {
          scrollToBottom();
        }
      }
      setError('');
    } catch (err: any) {
      if (!silent) setError(err.message || 'Failed to load messages');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [uid, type, playNotificationSound, scrollToBottom]);

  useEffect(() => {
    if (uid) loadMessages(false);
  }, [uid]);

  useEffect(() => {
    if (!uid) return;
    pollingRef.current = setInterval(() => { loadMessages(true); }, 10000);
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [uid, loadMessages]);

  // File handling
  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;
    const newFiles = Array.from(files);
    setSelectedFiles(prev => [...prev, ...newFiles]);

    // Generate previews
    newFiles.forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFilePreviews(prev => [...prev, e.target?.result as string]);
        };
        reader.readAsDataURL(file);
      } else {
        setFilePreviews(prev => [...prev, '']);
      }
    });

    setShowAttachMenu(false);
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setFilePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleGalleryClick = () => {
    fileInputRef.current?.click();
  };

  const handleCameraClick = () => {
    cameraInputRef.current?.click();
  };

  const handleSend = async () => {
    if ((!newMessage.trim() && selectedFiles.length === 0) || sending) return;
    setSending(true);
    try {
      let messageText = newMessage.trim();

      // Upload files first if any
      if (selectedFiles.length > 0) {
        const uploadPromises = selectedFiles.map(file =>
          followUpContestService.uploadChatFile(file, type)
        );
        const uploadResults = await Promise.all(uploadPromises);

        // Build attachment HTML to append to message
        const attachmentHtml = uploadResults.map((result: any) => {
          const filePath = result.FilePath || result.filePath;
          const fileName = result.FileName || result.fileName;
          const contentType = result.ContentType || result.contentType || '';
          const downloadUrl = followUpContestService.getDownloadUrl(filePath);

          if (contentType.startsWith('image/')) {
            return `<br/><img src="${downloadUrl}" alt="${fileName}" style="max-width:200px;max-height:200px;border-radius:8px;" />`;
          }
          return `<br/><a href="${downloadUrl}" target="_blank">${fileName}</a>`;
        }).join('');

        messageText = (messageText || '') + attachmentHtml;
      }

      if (!messageText) {
        setSending(false);
        return;
      }

      const sendFn = type === 'FollowUp'
        ? followUpContestService.sendFollowUpMessage({ ParentUID: uid, Message: messageText })
        : followUpContestService.sendContestMessage({ ParentUID: uid, Message: messageText });
      await sendFn;
      setNewMessage('');
      setSelectedFiles([]);
      setFilePreviews([]);
      await loadMessages(true);
      scrollToBottom();
    } catch (err: any) {
      setError(err.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleCloseConfirm = async () => {
    setClosing(true);
    try {
      const closeFn = type === 'FollowUp'
        ? followUpContestService.closeFollowUp(uid)
        : followUpContestService.closeContest(uid);
      await closeFn;
      setShowCloseConfirm(false);
      await loadMessages(true);
    } catch (err: any) {
      setError(err.message || 'Failed to close');
      setShowCloseConfirm(false);
    } finally {
      setClosing(false);
    }
  };

  const isMyMessage = (msg: any) => {
    const senderJP = msg.SenderJobPositionUID || msg.senderJobPositionUID || '';
    return senderJP === currentUserJPUID;
  };

  const formatTime = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (d.toDateString() === today.toDateString()) return 'Today';
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getMessageDate = (msg: any) => {
    const dt = msg.CreatedTime || msg.createdTime || '';
    return dt ? new Date(dt).toDateString() : '';
  };

  const headerStatus = header?.Status || header?.status || 'Open';
  const isOpen = headerStatus === 'Open';
  const formName = header?.FormName || header?.formName || '';
  const formNumber = header?.FormNumber || header?.formNumber || '';
  const customerName = header?.CustomerName || header?.customerName || '';
  const cdeaName = header?.CDEAName || header?.cdeaName || '';
  const chatTypeLabel = type === 'FollowUp' ? 'Follow-Up' : 'Contest';

  let lastDate = '';

  return (
    <PageContainer>
      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => handleFileSelect(e.target.files)}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={(e) => handleFileSelect(e.target.files)}
      />

      {/* Top header */}
      <TopHeader>
        <TopHeaderInner>
          <BackBtn onClick={() => navigate('/tickets-history')}>
            {React.createElement(FaArrowLeft as any, { size: 14 })}
          </BackBtn>
          <HeaderLogo src={cdePlusLogo} alt="CDE+" />
          <HeaderTitle>{chatTypeLabel} Chat</HeaderTitle>
          {header && <HeaderStatus status={headerStatus}>{headerStatus}</HeaderStatus>}
        </TopHeaderInner>
      </TopHeader>

      {/* Info banner */}
      {header && (
        <InfoBanner>
          <BannerCard>
            <BannerAvatar>
              {React.createElement(FaUserCircle as any, { size: 20 })}
            </BannerAvatar>
            <BannerDetails>
              <BannerFormNumber>
                {formNumber || formName}
              </BannerFormNumber>
              <BannerBottomRow>
                {[formName, customerName, cdeaName ? `CDEA: ${cdeaName}` : ''].filter(Boolean).join('  \u00B7  ')}
              </BannerBottomRow>
            </BannerDetails>
            {isOpen && (
              <CloseBtn onClick={() => setShowCloseConfirm(true)}>
                {React.createElement(FaTimesCircle as any, { size: 9 })} Close
              </CloseBtn>
            )}
          </BannerCard>
        </InfoBanner>
      )}

      {/* Chat */}
      <ContentContainer>
        <ChatMessagesContainer ref={chatContainerRef}>
          {loading ? (
            <LoadingText>Loading messages</LoadingText>
          ) : error ? (
            <LoadingText style={{ color: '#dc2626' }}>{error}</LoadingText>
          ) : messages.length === 0 ? (
            <EmptyChat>
              <EmptyChatIcon>
                {type === 'FollowUp' ? '\u{1F4AC}' : '\u{26A0}\uFE0F'}
              </EmptyChatIcon>
              <span>No messages yet</span>
              <span style={{ fontSize: '0.72rem', color: '#ccc' }}>Start the conversation below</span>
            </EmptyChat>
          ) : (
            messages.map((msg: any, index: number) => {
              const mine = isMyMessage(msg);
              const msgDate = getMessageDate(msg);
              let showDate = false;
              if (msgDate !== lastDate) {
                showDate = true;
                lastDate = msgDate;
              }
              const senderName = msg.SenderName || msg.senderName || 'Unknown';
              const senderInitial = senderName.charAt(0).toUpperCase();
              const messageHtml = msg.Message || msg.message || '';

              return (
                <React.Fragment key={msg.UID || msg.uid || index}>
                  {showDate && (
                    <DateSeparator>
                      <DateLabel>{formatDate(msg.CreatedTime || msg.createdTime)}</DateLabel>
                    </DateSeparator>
                  )}
                  <MessageCard isMine={mine}>
                    <CardHeader isMine={mine}>
                      <CardAvatar isMine={mine}>{senderInitial}</CardAvatar>
                      <CardSenderName isMine={mine}>{senderName}</CardSenderName>
                      <CardTime>{formatTime(msg.CreatedTime || msg.createdTime || '')}</CardTime>
                    </CardHeader>
                    <CardBody dangerouslySetInnerHTML={{ __html: messageHtml }} />
                  </MessageCard>
                </React.Fragment>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </ChatMessagesContainer>

        {isOpen && (
          <div ref={inputAreaRef} style={{ position: 'relative' }}>
            {/* Attachment popup */}
            {showAttachMenu && (
              <AttachPopup>
                <AttachOption onClick={handleCameraClick}>
                  {React.createElement(FaCamera as any, { size: 14, color: '#3b82f6' })}
                  Camera
                </AttachOption>
                <AttachOption onClick={handleGalleryClick}>
                  {React.createElement(FaImage as any, { size: 14, color: '#10b981' })}
                  Gallery
                </AttachOption>
              </AttachPopup>
            )}

            <InputArea>
              {/* File previews */}
              {selectedFiles.length > 0 && (
                <FilePreviewStrip>
                  {selectedFiles.map((file, i) => (
                    <FilePreviewItem key={i}>
                      {filePreviews[i] ? (
                        <FilePreviewThumb src={filePreviews[i]} alt={file.name} />
                      ) : (
                        React.createElement(FaFile as any, { size: 14, color: '#9ca3af' })
                      )}
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100px' }}>
                        {file.name}
                      </span>
                      <FileRemoveBtn onClick={() => removeFile(i)}>
                        {React.createElement(FaTimes as any, { size: 7 })}
                      </FileRemoveBtn>
                    </FilePreviewItem>
                  ))}
                </FilePreviewStrip>
              )}

              <InputRow>
                <IconBtn type="button" onClick={() => setShowAttachMenu(!showAttachMenu)}>
                  {React.createElement(FaPaperclip as any, { size: 15 })}
                </IconBtn>
                <MessageInput
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type a message..."
                  rows={1}
                />
                <SendButton onClick={handleSend} disabled={sending || (!newMessage.trim() && selectedFiles.length === 0)}>
                  {React.createElement(FaPaperPlane as any, { size: 12 })}
                </SendButton>
              </InputRow>
            </InputArea>
          </div>
        )}

        {!isOpen && !loading && (
          <ClosedBanner>
            This {chatTypeLabel.toLowerCase()} has been closed.
          </ClosedBanner>
        )}
      </ContentContainer>

      {/* Close Confirm Dialog */}
      {showCloseConfirm && (
        <DialogOverlay onClick={() => !closing && setShowCloseConfirm(false)}>
          <DialogBox onClick={(e) => e.stopPropagation()}>
            <DialogIcon>{React.createElement(FaTimesCircle as any, { size: 24, color: '#dc2626' })}</DialogIcon>
            <DialogTitle>Close {chatTypeLabel}?</DialogTitle>
            <DialogMessage>
              Once closed, no more messages can be sent. This action cannot be undone.
            </DialogMessage>
            <DialogButtons>
              <DialogCancelBtn onClick={() => setShowCloseConfirm(false)} disabled={closing}>
                Cancel
              </DialogCancelBtn>
              <DialogConfirmBtn onClick={handleCloseConfirm} disabled={closing}>
                {closing ? 'Closing...' : 'Yes, Close'}
              </DialogConfirmBtn>
            </DialogButtons>
          </DialogBox>
        </DialogOverlay>
      )}
    </PageContainer>
  );
};

export default FollowUpContestChat;
